import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSalesForecast, useSalesTrends, useSeasonality } from "@/hooks/useSalesForecast";
import { TrendingUp, TrendingDown, Activity, Calendar, BarChart3 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import SalesKPICard from "@/components/sales-trends/SalesKPICard";
import ForecastSection from "@/components/sales-trends/ForecastSection";
import TrendsSection from "@/components/sales-trends/TrendsSection";
import SeasonalitySection from "@/components/sales-trends/SeasonalitySection";

export default function SalesTrendsAnalysis() {
  const { data: forecast, isLoading: forecastLoading } = useSalesForecast(6);
  const { data: trends, isLoading: trendsLoading } = useSalesTrends('month', 12);
  const { data: seasonality, isLoading: seasonalityLoading } = useSeasonality();

  if (forecastLoading || trendsLoading || seasonalityLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const currentRevenue = trends?.[trends.length - 1]?.revenue || 0;
  const previousRevenue = trends?.[trends.length - 2]?.revenue || 0;
  const revenueChange = currentRevenue - previousRevenue;
  const revenueChangePercent = previousRevenue ? ((revenueChange / previousRevenue) * 100).toFixed(1) : 0;

  const avgGrowthRate = trends?.reduce((acc, t) => acc + (t.growth_rate || 0), 0) / (trends?.length || 1);
  const totalBookings = trends?.reduce((acc, t) => acc + t.bookings, 0) || 0;

  const predictedRevenueTotal = forecast?.reduce((acc, f) => acc + f.predicted_revenue, 0) || 0;
  const avgConfidence = forecast?.reduce((acc, f) => acc + f.confidence_level, 0) / (forecast?.length || 1) || 0;

  const combinedData = [
    ...(trends?.map(t => ({
      period: t.period_label,
      revenue: t.revenue,
      bookings: t.bookings,
      type: 'historical'
    })) || []),
    ...(forecast?.map(f => ({
      period: f.forecast_month.substring(0, 7),
      revenue: f.predicted_revenue,
      bookings: f.predicted_bookings,
      type: 'forecast'
    })) || [])
  ];

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <div>
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          تحليل اتجاهات المبيعات والتنبؤات
        </h1>
        <p className="text-muted-foreground">
          تحليل شامل للأداء التاريخي والتنبؤات المستقبلية
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <SalesKPICard
          title="الإيرادات الحالية"
          value={`${currentRevenue.toLocaleString('ar-SA')} ر.س`}
          icon={revenueChange >= 0 ? TrendingUp : TrendingDown}
          subtitle={`${revenueChangePercent}% عن الشهر السابق`}
          borderColor="border-l-primary"
        />
        
        <SalesKPICard
          title="معدل النمو"
          value={`${avgGrowthRate.toFixed(1)}%`}
          icon={Activity}
          subtitle="متوسط آخر 12 شهر"
          borderColor="border-l-chart-2"
        />
        
        <SalesKPICard
          title="التنبؤات (6 أشهر)"
          value={`${predictedRevenueTotal.toLocaleString('ar-SA')} ر.س`}
          icon={Calendar}
          subtitle={`دقة ${(avgConfidence * 100).toFixed(0)}%`}
          borderColor="border-l-chart-3"
        />
        
        <SalesKPICard
          title="إجمالي الحجوزات"
          value={totalBookings.toLocaleString('ar-SA')}
          icon={BarChart3}
          subtitle="آخر 12 شهر"
          borderColor="border-l-chart-4"
        />
      </div>

      <Tabs defaultValue="forecast" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="forecast">التنبؤات</TabsTrigger>
          <TabsTrigger value="trends">الاتجاهات</TabsTrigger>
          <TabsTrigger value="seasonality">الموسمية</TabsTrigger>
        </TabsList>

        <TabsContent value="forecast" className="space-y-4">
          <ForecastSection 
            forecast={forecast} 
            combinedData={combinedData}
            avgConfidence={avgConfidence}
          />
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <TrendsSection trends={trends} />
        </TabsContent>

        <TabsContent value="seasonality" className="space-y-4">
          <SeasonalitySection seasonality={seasonality} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
